import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemText,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip
} from "@mui/material";
import { FaArrowLeft, FaEye, FaArrowRight, FaPlus, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { MdOutlineTextsms } from "react-icons/md";
import { getData, postData } from "../../../Webservice/Webservice";
import { useNavigate } from "react-router-dom";

function SMS() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Campaign Form State
  const [formData, setFormData] = useState({
    name: "",
    templateId: "",
    variableValues: {},
    scheduledAt: null,
    isScheduled: false,
    date: ""
  });

  // Template Form State
  const [templateFormData, setTemplateFormData] = useState({
    name: "",
    templateId: "",
    content: "",
    variables: "" // Comma separated e.g. "var1,var2"
  });

  // Customer Filter State
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [filterData, setFilterData] = useState({
    promotionType: "sms",
    city: [],
    gender: [],
    status: [],
    customerType: []
  });

  const tokenValue = sessionStorage.getItem("token");
  const token = tokenValue ? `Bearer ${tokenValue}` : "";

  // Initialization
  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchCities();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await getData("sms-campaign/list", null, token);
      if (res.success) setCampaigns(res.campaigns);
    } catch (err) { console.error(err); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await getData("sms-campaign/templates", null, token);
      if (res.success) setTemplates(res.templates);
    } catch (err) { console.error(err); }
  };

  const fetchCities = async () => {
    try {
      const response = await getData("campaign/city/list", null, token);
      setCities(response.cities || []);
    } catch (error) { console.error(error); }
  };

  const fetchCustomers = async (filters) => {
    setLoading(true);
    try {
      const response = await postData("campaign/customer/list", filters, token);
      if (response && response.customers) {
        // Filter out customers without mobile numbers and De-duplicate
        const uniqueMap = new Map();
        response.customers.forEach(c => {
          if (c.mobile && c.mobile.trim() !== "" && !uniqueMap.has(c.mobile)) {
            uniqueMap.set(c.mobile, c);
          }
        });
        const uniqueCustomers = Array.from(uniqueMap.values());

        
        setCustomers(uniqueCustomers);
        setSelectedCustomers(uniqueCustomers.map(c => c._id));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  // Handlers

  const handleTemplateChange = (e) => {
    const tId = e.target.value;
    const template = templates.find(t => t._id === tId);
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      templateId: tId,
      variableValues: template?.variables?.reduce((acc, curr) => ({ ...acc, [curr]: "" }), {}) || {}
    }));
  };

  const handleVariableChange = (varName, value) => {
    setFormData(prev => ({
      ...prev,
      variableValues: { ...prev.variableValues, [varName]: value }
    }));
  };

  const handleApplyFilters = async () => {
    const { customerType, ...filters } = filterData;
    await fetchCustomers(filters);
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!selectedCustomers.length) return alert("Please select customers");
    
    const payload = {
        name: formData.name,
        templateId: formData.templateId,
        customerIds: selectedCustomers,
        variableValues: formData.variableValues,
        scheduledAt: formData.isScheduled ? formData.date : null
    };

    try {
        setLoading(true);
        const res = await postData("sms-campaign/create", payload, token);
        if (res.success) {
            setShowForm(false);
            fetchCampaigns();
            resetForm();
        }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    const payload = {
        ...templateFormData,
        variables: templateFormData.variables.split(",").map(v => v.trim()).filter(Boolean)
    };
    try {
        const res = await postData("sms-campaign/templates", payload, token);
        if (res.success) {
            setShowTemplateForm(false);
            fetchTemplates();
            setTemplateFormData({ name: "", templateId: "", content: "", variables: "" });
        }
    } catch (err) { console.error(err); }
  };

  const resetForm = () => {
    setFormData({ name: "", templateId: "", variableValues: {}, scheduledAt: null, isScheduled: false, date: "" });
    setSelectedCustomers([]);
    setSelectedTemplate(null);
  };

  const getStatusChip = (status) => {
    const colors = {
        pending: "default",
        scheduled: "info",
        running: "warning",
        completed: "success",
        cancelled: "error"
    };
    return <Chip label={status.toUpperCase()} size="small" color={colors[status] || "default"} sx={{ fontWeight: 800, borderRadius: '6px' }} />;
  };

  if (showForm) {
    return (
      <Box sx={{ p: 4, maxWidth: 1200, margin: "0 auto" }}>
        <Card sx={{ borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.1)", border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={4}>
              <IconButton onClick={() => setShowForm(false)} sx={{ mr: 2, color: "black" }}><FaArrowLeft /></IconButton>
              <Typography variant="h4" sx={{ fontFamily: "Mulish, sans-serif", fontWeight: 700 }}>New SMS Campaign</Typography>
            </Box>
            
            <form onSubmit={handleCreateCampaign}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth required label="Campaign Name" 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})} 
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Select MSG91 Template</InputLabel>
                    <Select value={formData.templateId} onChange={handleTemplateChange} label="Select MSG91 Template">
                      {templates.map(t => <MenuItem key={t._id} value={t._id}>{t.name} ({t.templateId})</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedTemplate && selectedTemplate.variables.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Template Variables</Typography>
                    <Grid container spacing={2}>
                      {selectedTemplate.variables.map(v => (
                        <Grid item xs={12} md={4} key={v}>
                          <TextField 
                            fullWidth label={`Value for ${v}`} 
                            value={formData.variableValues[v] || ""} 
                            onChange={e => handleVariableChange(v, e.target.value)}
                            placeholder={`Enter ${v}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Audience Filtering</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>City</InputLabel>
                                <Select multiple value={selectedCity} onChange={e => { setSelectedCity(e.target.value); setFilterData({...filterData, city: e.target.value})}} renderValue={s => s.join(", ")}>
                                    {cities.map(c => <MenuItem key={c} value={c}><Checkbox checked={selectedCity.includes(c)} /><ListItemText primary={c} /></MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select multiple value={filterData.gender} onChange={e => setFilterData({...filterData, gender: e.target.value})} renderValue={s => s.join(", ")}>
                                    <MenuItem value="male"><Checkbox checked={filterData.gender.includes("male")} />Male</MenuItem>
                                    <MenuItem value="female"><Checkbox checked={filterData.gender.includes("female")} />Female</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button fullWidth variant="outlined" onClick={handleApplyFilters} sx={{ height: '56px' }} disabled={loading}>
                                {loading ? <CircularProgress size={24} /> : <>Apply Filters <FaArrowRight style={{marginLeft: 8}} /></>}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Selected: <strong>{selectedCustomers.length}</strong> unique customers
                            </Typography>
                        </Grid>
                    </Grid>

                    {customers.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Recipient List Preview</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250, borderRadius: '8px' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700 }}>Name</TableCell>
                                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700 }}>Mobile</TableCell>
                                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700, width: 80 }}>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {customers.map((c) => (
                                            <TableRow key={c._id}>
                                                <TableCell>{c.name || "N/A"}</TableCell>
                                                <TableCell>{c.mobile}</TableCell>
                                                <TableCell>
                                                    <Checkbox 
                                                        size="small"
                                                        checked={selectedCustomers.includes(c._id)} 
                                                        onChange={() => {
                                                            setSelectedCustomers(prev => 
                                                                prev.includes(c._id) ? prev.filter(id => id !== c._id) : [...prev, c._id]
                                                            );
                                                        }} 
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Grid>


                <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox checked={formData.isScheduled} onChange={e => setFormData({...formData, isScheduled: e.target.checked})} />} label="Schedule for later" />
                    {formData.isScheduled && (
                        <TextField fullWidth type="datetime-local" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} />
                    )}
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button variant="outlined" onClick={() => setShowForm(false)} sx={{ px: 4 }}>Cancel</Button>
                  <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "rgb(255, 209, 2)", color: "black", px: 6, fontWeight: 700 }}>
                    {loading ? "Creating..." : "Launch Campaign"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, margin: "0 auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: "Mulish, sans-serif" }}>SMS Campaigns</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<FaPlus />} onClick={() => setShowTemplateForm(true)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>Manage Templates</Button>
            <Button variant="contained" startIcon={<MdOutlineTextsms />} onClick={() => setShowForm(true)} sx={{ backgroundColor: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', textTransform: 'none', fontWeight: 700, border: '1px solid black' }}>New Campaign</Button>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Campaign History" />
        <Tab label="Templates" />
      </Tabs>

      {activeTab === 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f8f9fa', fontWeight: 800, fontFamily: 'Mulish' } }}>
                <TableCell>Campaign ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent / Failed</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map(c => (
                <TableRow key={c._id} hover>
                  <TableCell><strong>{c.campId}</strong></TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.templateId?.name || "N/A"}</TableCell>
                  <TableCell>{c.stats.total} Customers</TableCell>
                  <TableCell>{getStatusChip(c.status)}</TableCell>
                  <TableCell>
                    <span style={{ color: 'green' }}>{c.stats.sent}</span> / <span style={{ color: 'red' }}>{c.stats.failed}</span>
                  </TableCell>
                  <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 && <TableRow><TableCell colSpan={7} align="center">No campaigns found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={3}>
            {templates.map(t => (
                <Grid item xs={12} md={4} key={t._id}>
                    <Card sx={{ height: '100%', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t.name}</Typography>
                            <Typography variant="caption" color="text.secondary">ID: {t.templateId}</Typography>
                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: '8px', minHeight: '60px' }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t.content || "No preview available"}</Typography>
                            </Box>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {t.variables.map(v => <Chip key={v} label={`##${v}##`} size="small" sx={{ fontSize: '0.7rem' }} />)}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
            <Grid item xs={12} md={4}>
                <Box onClick={() => setShowTemplateForm(true)} sx={{ height: '100%', border: '2px dashed #ccc', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, cursor: 'pointer', '&:hover': { borderColor: 'black', backgroundColor: '#fafafa' } }}>
                    <FaPlus size={30} color="#666" />
                    <Typography sx={{ mt: 2, fontWeight: 700 }}>Add New Template</Typography>
                </Box>
            </Grid>
        </Grid>
      )}

      {/* Add Template Dialog */}
      <Dialog open={showTemplateForm} onClose={() => setShowTemplateForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Add MSG91 Flow Template</DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Register your approved DLT template here to use it in campaigns.</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <TextField fullWidth label="Template Name (Internal)" value={templateFormData.name} onChange={e => setTemplateFormData({...templateFormData, name: e.target.value})} placeholder="e.g. Diwali Promo 2024" />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth label="MSG91 Template ID" value={templateFormData.templateId} onChange={e => setTemplateFormData({...templateFormData, templateId: e.target.value})} placeholder="e.g. 660c...f12" />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth multiline rows={3} label="Template Content (For reference)" value={templateFormData.content} onChange={e => setTemplateFormData({...templateFormData, content: e.target.value})} placeholder="Paste the approved DLT text here..." />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth label="Variables (Comma separated)" value={templateFormData.variables} onChange={e => setTemplateFormData({...templateFormData, variables: e.target.value})} placeholder="e.g. var1,var2,customer_name" />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowTemplateForm(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateTemplate} sx={{ backgroundColor: 'black', color: 'white', px: 4 }}>Save Template</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SMS;
